import { useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useAppDialog } from "@pepsico/layout";
import { usePagePrefs } from "@pepsico/pref-permissions";
import { useQueryClient } from "@tanstack/react-query";
import { noop } from "es-toolkit";

import { useGetGrowersGeoByLocation } from "api/growers-geo/growers-geo";
import type { GrowerGeoResponse, SiteGeoResponse } from "api/model";
import {
  getGetSitesGeoByLocationQueryKey,
  useGetSitesGeoByLocation,
  useUpdateSiteGeoBoundary,
} from "api/sites-geo/sites-geo";
import { hasBoundaryGeoJson } from "context/PlotMap/boundaryUtils";
import { getApiErrorMessage } from "utils";

const getAssignSitesError = getApiErrorMessage(
  "Failed to assign sites to grower boundaries.",
);

interface AssignSitesToGrowersEditorProps {
  onCancel: () => void;
  open: boolean;
}

const getGrowerLabel = (grower: GrowerGeoResponse): string =>
  grower.grower_label?.trim() || "Unlabelled Grower Boundary";

const getSiteLabel = (site: SiteGeoResponse): string =>
  site.site_label?.trim() || "Unlabelled Site Boundary";

const AssignSitesToGrowersEditor: React.FC<AssignSitesToGrowersEditorProps> = ({
  onCancel,
  open,
}) => {
  const { setAppDialog } = useAppDialog();
  const queryClient = useQueryClient();
  const [prefs] = usePagePrefs();
  const [selectedGrowerId, setSelectedGrowerId] = useState<number | "">("");
  const [selectedSites, setSelectedSites] = useState<SiteGeoResponse[]>([]);

  const locationUuid = (prefs.location as string | null | undefined) ?? "";
  const { data: growers = [] } = useGetGrowersGeoByLocation(
    { location_uuid: locationUuid },
    {
      query: {
        enabled: locationUuid !== "",
        select: (res): GrowerGeoResponse[] =>
          res.status === 200 ? res.data : [],
      },
    },
  );
  const { data: sites = [] } = useGetSitesGeoByLocation(undefined, {
    query: {
      select: (res): SiteGeoResponse[] => (res.status === 200 ? res.data : []),
    },
  });
  const { isPending: isSaving, mutateAsync: updateSiteGeo } =
    useUpdateSiteGeoBoundary();

  const growersWithBoundary = growers.filter((grower) =>
    hasBoundaryGeoJson(grower.boundary),
  );
  const unassignedSites = sites.filter((site) => site.grower_id == null);
  const hasSingleGrowerBoundary = growersWithBoundary.length === 1;
  const canSave =
    selectedGrowerId !== "" && selectedSites.length > 0 && !isSaving;

  useEffect(() => {
    const withBoundary = growers.filter((grower) =>
      hasBoundaryGeoJson(grower.boundary),
    );
    if (withBoundary.length === 1) {
      setSelectedGrowerId(withBoundary[0].id);
      return;
    }
    setSelectedGrowerId((current) =>
      current !== "" && withBoundary.some((grower) => grower.id === current)
        ? current
        : "",
    );
  }, [growers]);

  useEffect(() => {
    const unassignedById = new Map(
      sites
        .filter((site) => site.grower_id == null)
        .map((site) => [site.id, site]),
    );
    setSelectedSites((current) =>
      current.flatMap((site) => {
        const next = unassignedById.get(site.id);
        return next == null ? [] : [next];
      }),
    );
  }, [sites]);

  const handleCancel = () => {
    if (isSaving) {
      return;
    }

    setSelectedSites([]);
    setSelectedGrowerId(
      growersWithBoundary.length === 1 ? growersWithBoundary[0].id : "",
    );
    onCancel();
  };

  const handleSave = async () => {
    if (selectedGrowerId === "" || selectedSites.length === 0) {
      return;
    }

    try {
      for (const site of selectedSites) {
        const response = await updateSiteGeo({
          data: { grower_id: selectedGrowerId },
          siteGeoId: site.id,
        });
        if (response.status !== 200) {
          setAppDialog({
            confirmText: "Close",
            message: <>{getAssignSitesError(response.data)}</>,
            onConfirm: noop,
            open: true,
            title: "Error",
          });
          return;
        }
      }

      await queryClient.invalidateQueries({
        queryKey: getGetSitesGeoByLocationQueryKey(),
      });
      onCancel();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to assign sites to grower boundaries.";
      setAppDialog({
        confirmText: "Close",
        message: <>{message}</>,
        onConfirm: noop,
        open: true,
        title: "Error",
      });
    }
  };

  return (
    <Dialog fullWidth maxWidth="sm" onClose={handleCancel} open={open}>
      <DialogTitle>Assign sites</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          <Typography color="text.secondary" variant="body2">
            Choose a grower boundary and the sites to assign to it.
          </Typography>
          <FormControl
            disabled={hasSingleGrowerBoundary || isSaving}
            fullWidth
            size="small"
          >
            <InputLabel id="assign-sites-grower-boundary-label">
              Grower boundary
            </InputLabel>
            <Select
              aria-label="Grower boundary"
              disabled={hasSingleGrowerBoundary || isSaving}
              label="Grower boundary"
              labelId="assign-sites-grower-boundary-label"
              onChange={(event) => {
                const value = event.target.value;
                setSelectedGrowerId(value === "" ? "" : Number(value));
              }}
              value={selectedGrowerId === "" ? "" : String(selectedGrowerId)}
            >
              {growersWithBoundary.map((grower) => (
                <MenuItem key={grower.id} value={String(grower.id)}>
                  {getGrowerLabel(grower)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {unassignedSites.length === 0 ? (
            <Typography color="text.secondary" variant="body2">
              All sites are assigned to a grower boundary.
            </Typography>
          ) : (
            <Autocomplete
              aria-label="Sites to assign"
              disableCloseOnSelect
              disabled={isSaving}
              getOptionKey={(site) => site.id}
              getOptionLabel={getSiteLabel}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              limitTags={2}
              multiple
              onChange={(_, value) => {
                setSelectedSites(value);
              }}
              options={unassignedSites}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Sites"
                  placeholder="Search sites"
                  size="small"
                />
              )}
              size="small"
              slotProps={{ chip: { size: "small" } }}
              value={selectedSites}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          aria-label="Assign selected sites to grower boundary"
          disabled={!canSave}
          loading={isSaving}
          onClick={() => {
            void handleSave();
          }}
          size="small"
          variant="text"
        >
          {isSaving ? "Assigning…" : "Assign"}
        </Button>
        <Button
          aria-label="Cancel assigning sites"
          color="error"
          disabled={isSaving}
          onClick={handleCancel}
          size="small"
          variant="text"
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignSitesToGrowersEditor;
